import type { IObservableArray } from 'mobx';
import type { JSX, ReactNode } from 'react';
import React, { createContext, useContext, useMemo } from 'react';

import type { Goal } from '@feathr/blackbox';

interface IGoalContext {
  disabled: boolean;
  goals: IObservableArray<Goal>;
  handleRemove: (goal: Goal) => void;
  showWarning: boolean;
}

interface IGoalProviderProps {
  children: ReactNode;
  disabled: boolean;
  goals: IObservableArray<Goal>;
  handleRemove: (goal: Goal) => void;
  showWarning: boolean;
}

const GoalContext = createContext<IGoalContext | undefined>(undefined);

function GoalProvider({
  children,
  disabled,
  goals,
  handleRemove,
  showWarning,
}: Readonly<IGoalProviderProps>): JSX.Element {
  const memoizedValue = useMemo(
    () => ({
      disabled,
      goals,
      handleRemove,
      showWarning,
    }),
    [disabled, goals, handleRemove, showWarning],
  );

  return <GoalContext.Provider value={memoizedValue}>{children}</GoalContext.Provider>;
}

function useGoalContext(): IGoalContext {
  const context = useContext(GoalContext);
  if (context === undefined) {
    throw new Error('useGoalContext must be used within a GoalProvider');
  }
  return context;
}

export { GoalProvider, useGoalContext };
